<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    :dir="$i18n?.locale === 'he' ? 'rtl' : 'ltr'"
  >
    <template #activator="{ props }">
      <v-text-field
        v-model="display"
        class="pa-2"
        :label="label"
        readonly
        dense
        outlined
        hide-details
        v-bind="props"
        :class="{ 'rtl-input': $i18n?.locale === 'he' }"
      />
    </template>
    <v-card :dir="$i18n?.locale === 'he' ? 'rtl' : 'ltr'">
      <v-tabs v-model="pickerTab" color="primary" grow>
        <v-tab value="date">{{ $t("apiManager.date") }}</v-tab>
        <v-tab value="time">{{ $t("apiManager.time") }}</v-tab>
      </v-tabs>
      <v-window v-model="pickerTab">
        <v-window-item value="date">
          <v-date-picker
            v-model="date"
            show-adjacent-months
            :min="minDate"
            :max="maxDate"
          />
        </v-window-item>
        <v-window-item value="time">
          <v-time-picker v-model="time" format="24hr" />
        </v-window-item>
      </v-window>
      <v-card-actions>
        <v-btn text @click="menu = false">{{ $t("close") }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: "DateTimePicker",
  props: {
    label: { type: String, required: true },
    modelValue: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    const pad = (n) => String(n).padStart(2, "0");
    const now = new Date();
    const maxDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}`;
    const minDateObj = new Date(now);
    minDateObj.setFullYear(now.getFullYear() - 1);
    const minDate = `${minDateObj.getFullYear()}-${pad(
      minDateObj.getMonth() + 1
    )}-${pad(minDateObj.getDate())}`;
    return {
      menu: false,
      pickerTab: "date",
      date: null,
      time: null,
      display: "",
      minDate,
      maxDate,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (val) {
          const d = new Date(val);
          if (!isNaN(d)) {
            this.date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            this.time = `${String(d.getHours()).padStart(2, "0")}:${String(
              d.getMinutes()
            ).padStart(2, "0")}`;
            const iso = this.formatDateTime(d);
            this.display = iso;
            if (val !== iso) {
              this.$emit("update:modelValue", iso);
            }
          }
        } else {
          this.date = null;
          this.time = null;
          this.display = "";
        }
      },
    },
    date(val) {
      this.emitChange();
    },
    time(val) {
      this.emitChange();
    },
  },
  methods: {
    emitChange() {
      if (this.date && this.time) {
        const [hours, minutes] = this.time.split(":");
        const dt = new Date(
          this.date.getFullYear(),
          this.date.getMonth(),
          this.date.getDate(),
          Number(hours),
          Number(minutes),
          0
        );
        const iso = this.formatDateTime(dt);
        this.display = iso;
        this.$emit("update:modelValue", iso);
      } else if (this.date) {
        this.display = this.formatDateOnly(this.date);
        this.$emit("update:modelValue", this.display);
      } else if (this.time) {
        this.display = this.formatTimeOnly(this.time);
        this.$emit("update:modelValue", this.display);
      } else {
        this.display = "";
        this.$emit("update:modelValue", "");
      }
    },
    formatDateTime(date) {
      if (!(date instanceof Date)) date = new Date(date);
      const pad = (n) => String(n).padStart(2, "0");
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      const offsetMinutes = -date.getTimezoneOffset();
      const sign = offsetMinutes >= 0 ? "+" : "-";
      const offsetHrs = pad(Math.floor(Math.abs(offsetMinutes) / 60));
      const offsetMins = pad(Math.abs(offsetMinutes) % 60);
      const offset = `${sign}${offsetHrs}:${offsetMins}`;
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`;
    },
    formatDateOnly(date) {
      if (!(date instanceof Date)) date = new Date(date);
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}`;
    },
    formatTimeOnly(time) {
      return time.length === 5 ? `${time}:00` : time;
    },
  },
};
</script>
